import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useNavigate, Link } from 'react-router'
import {
  Box,
  Typography,
  Button,
  Snackbar,
  Alert,
  Badge,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  CardActionArea,
  Stack,
  Checkbox,
  FormControlLabel,
  Switch,
  Divider,
  Tab,
  Tabs
} from '@mui/material'
import LogoutIcon from '@mui/icons-material/Logout'
import SupportAgentIcon from '@mui/icons-material/SupportAgent'
import PersonIcon from '@mui/icons-material/Person'
import AllowlistManager from '../components/AllowlistManager'
import BankDetailsForm from '../components/BankDetailsForm'
import CreditBalance from '../components/CreditBalance'
import Wrapper from '../components/Wrapper'
import { InviteCard } from '../components/InviteCard'
import routes from './routes'
import { signOut } from '../services/authService'
import { getHostedDives } from '../services/diveService'
import { getApprovedCount } from '../services/joinRequestService'
import { getInvitesForUser } from '../services/inviteService'
import { updateGuidedDiveFlag } from '../services/profileService'
import { isDivemasterLevel } from '../constants/accreditationLevels'
import { useSessionStore } from '../state'
import { useInviteStore, EnrichedInvite } from '../stores/inviteStore'
import { getFirestore, doc, getDoc } from 'firebase/firestore'
import Database from '../types/Database'

type AccountTab = 'dives' | 'invites' | 'bank'

const Account = () => {
  const navigate = useNavigate()
  const user = useSessionStore(s => s.user)
  const profile = useSessionStore(s => s.profile)
  const clearSession = useSessionStore(s => s.clearSession)
  const setProfile = useSessionStore(s => s.setProfile)
  const [error, setError] = useState<string | null>(null)
  const [myDives, setMyDives] = useState<Database.Dive[]>([])
  const [approvedCounts, setApprovedCounts] = useState<Record<string, number>>(
    {}
  )
  const [divesLoading, setDivesLoading] = useState(true)
  const [showAll, setShowAll] = useState(false)
  const [visibleCount, setVisibleCount] = useState(10)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const [guidedToggleSaving, setGuidedToggleSaving] = useState(false)
  const [tab, setTab] = useState<AccountTab>('dives')

  // Invites state
  const invites = useInviteStore(s => s.invites)
  const invitesLoading = useInviteStore(s => s.isLoading)
  const invitesError = useInviteStore(s => s.error)
  const setInvites = useInviteStore(s => s.setInvites)
  const setInvitesLoading = useInviteStore(s => s.setLoading)
  const setInvitesError = useInviteStore(s => s.setError)

  const canOfferGuidedDives =
    profile?.accreditation?.status === 'verified' &&
    isDivemasterLevel(
      profile.accreditation.type,
      profile.accreditation.level ?? ''
    )

  const filteredDives = useMemo(() => {
    if (showAll) return myDives
    const now = new Date()
    return myDives.filter(
      dive => dive.status !== 'cancelled' && dive.dateTime.toDate() > now
    )
  }, [myDives, showAll])

  const visibleDives = filteredDives.slice(0, visibleCount)
  const hasMore = visibleCount < filteredDives.length

  useEffect(() => {
    setVisibleCount(10)
  }, [showAll])

  useEffect(() => {
    if (!sentinelRef.current || !hasMore) return

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          setVisibleCount(prev => prev + 10)
        }
      },
      { threshold: 0.1 }
    )

    observer.observe(sentinelRef.current)
    return () => observer.disconnect()
  }, [hasMore, filteredDives.length])

  useEffect(() => {
    if (!user) return
    getHostedDives(user.uid)
      .then(async dives => {
        setMyDives(dives)
        const counts: Record<string, number> = {}
        await Promise.all(
          dives.map(async dive => {
            try {
              counts[dive.id] = await getApprovedCount(dive.id)
            } catch {
              counts[dive.id] = 0
            }
          })
        )
        setApprovedCounts(counts)
      })
      .catch(() => {})
      .finally(() => setDivesLoading(false))
  }, [user])

  const fetchInvites = useCallback(async () => {
    if (!user) return

    setInvitesLoading(true)
    setInvitesError(null)

    try {
      const rawInvites = await getInvitesForUser(user.uid)
      const db = getFirestore()

      const enriched: EnrichedInvite[] = await Promise.all(
        rawInvites.map(async (invite: Database.Invite) => {
          let diveSiteName = 'Unknown site'
          let diveDateTime = invite.createdAt
          let hostDisplayName = 'Unknown host'

          try {
            const diveSnap = await getDoc(doc(db, 'dives', invite.diveId))
            if (diveSnap.exists()) {
              const diveData = diveSnap.data()
              diveSiteName = diveData.siteName || 'Unknown site'
              diveDateTime = diveData.dateTime || invite.createdAt
            }
          } catch {
            // Keep defaults on failure
          }

          try {
            const hostSnap = await getDoc(doc(db, 'users', invite.inviterId))
            if (hostSnap.exists()) {
              const hostData = hostSnap.data()
              hostDisplayName = hostData.displayName || 'Unknown host'
            }
          } catch {
            // Keep defaults on failure
          }

          return {
            ...invite,
            diveSiteName,
            diveDateTime,
            hostDisplayName,
            distanceKm: null
          }
        })
      )

      setInvites(enriched)
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load invites'
      setInvitesError(message)
    } finally {
      setInvitesLoading(false)
    }
  }, [user, setInvites, setInvitesLoading, setInvitesError])

  useEffect(() => {
    if (tab === 'invites') fetchInvites()
  }, [tab, fetchInvites])

  const handleSignOut = async () => {
    try {
      await signOut()
      clearSession()
      navigate(routes.signin)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign out failed'
      setError(message)
    }
  }

  const availableTabs: { value: AccountTab; label: string }[] = [
    { value: 'dives', label: 'My Dives' },
    { value: 'invites', label: 'Invites' }
  ]
  if (profile?.guidedDiveFlag) {
    availableTabs.push({ value: 'bank', label: 'Bank Details' })
  }

  const renderDivesTab = () => (
    <>
      <Box sx={{ mt: 2 }}>
        {divesLoading ? (
          <CircularProgress size={24} />
        ) : myDives.length === 0 ? (
          <Typography variant='body2' color='text.secondary'>
            You haven&apos;t hosted any dives yet.
          </Typography>
        ) : (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showAll}
                  onChange={e => setShowAll(e.target.checked)}
                  size='small'
                />
              }
              label={<Typography variant='body2'>Show all dives</Typography>}
              sx={{ mb: 1 }}
            />
            {filteredDives.length === 0 ? (
              <Typography variant='body2' color='text.secondary'>
                No upcoming dives. Check &apos;Show all dives&apos; to see past
                dives.
              </Typography>
            ) : (
              <>
                <Stack spacing={1}>
                  {visibleDives.map(dive => (
                    <Card key={dive.id} variant='outlined'>
                      <CardActionArea component={Link} to={`/dives/${dive.id}`}>
                        <CardContent
                          sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}
                          >
                            <Box>
                              <Typography
                                variant='body1'
                                sx={{ fontWeight: 500 }}
                              >
                                {dive.siteName}
                              </Typography>
                              <Typography
                                variant='caption'
                                color='text.secondary'
                              >
                                {dive.dateTime
                                  .toDate()
                                  .toLocaleDateString(undefined, {
                                    dateStyle: 'medium'
                                  })}{' '}
                                · {approvedCounts[dive.id] ?? 0}/
                                {dive.maxParticipants} spots filled
                              </Typography>
                            </Box>
                            <Stack
                              direction='row'
                              spacing={1}
                              alignItems='center'
                            >
                              {(dive.pendingRequestCount ?? 0) > 0 && (
                                <Badge
                                  badgeContent={dive.pendingRequestCount}
                                  color='error'
                                >
                                  <Chip
                                    label='Requests'
                                    size='small'
                                    component={Link}
                                    to={`/dives/${dive.id}/requests`}
                                    clickable
                                    color='primary'
                                    variant='outlined'
                                  />
                                </Badge>
                              )}
                              <Chip
                                label={dive.status}
                                size='small'
                                color={
                                  dive.status === 'open'
                                    ? 'success'
                                    : dive.status === 'full'
                                    ? 'warning'
                                    : dive.status === 'cancelled'
                                    ? 'error'
                                    : 'default'
                                }
                              />
                            </Stack>
                          </Box>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  ))}
                </Stack>
                {hasMore && (
                  <Box
                    ref={sentinelRef}
                    sx={{ display: 'flex', justifyContent: 'center', py: 2 }}
                  >
                    <CircularProgress size={20} />
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Box>

      <Box sx={{ mt: 4 }}>
        <AllowlistManager />
      </Box>

      {canOfferGuidedDives && (
        <Box sx={{ mt: 4 }}>
          <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 1 }}>
            Guided Dives
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={profile?.guidedDiveFlag ?? false}
                disabled={guidedToggleSaving}
                onChange={async e => {
                  if (!user) return
                  const newValue = e.target.checked
                  setGuidedToggleSaving(true)
                  try {
                    await updateGuidedDiveFlag(user.uid, newValue)
                    if (profile) {
                      setProfile({ ...profile, guidedDiveFlag: newValue })
                    }
                  } catch {
                    setError('Failed to update guided dives preference')
                  } finally {
                    setGuidedToggleSaving(false)
                  }
                }}
              />
            }
            label='I want to offer guided dives'
          />
        </Box>
      )}
    </>
  )

  const renderInvitesTab = () => {
    if (invitesLoading && invites.length === 0) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '30vh'
          }}
        >
          <CircularProgress />
        </Box>
      )
    }

    return (
      <Box sx={{ mt: 2 }}>
        <Stack spacing={2}>
          {invitesError && (
            <Alert
              severity='error'
              action={
                <Button color='inherit' size='small' onClick={fetchInvites}>
                  Retry
                </Button>
              }
            >
              {invitesError}
            </Alert>
          )}

          {!invitesError && invites.length === 0 && !invitesLoading && (
            <Typography
              variant='body1'
              color='text.secondary'
              sx={{ textAlign: 'center', mt: 4 }}
            >
              No pending invitations
            </Typography>
          )}

          {invites.length > 0 && (
            <Stack spacing={2}>
              {invites.map(invite => (
                <InviteCard key={invite.id} invite={invite} />
              ))}
            </Stack>
          )}
        </Stack>
      </Box>
    )
  }

  const renderBankTab = () => (
    <Box sx={{ mt: 2 }}>
      <CreditBalance creditBalance={profile?.creditBalance ?? 0} />

      {profile?.bankDetails && (
        <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
          Current account: ****
          {profile.bankDetails.accountNumber.slice(-4)}
        </Typography>
      )}
      {user && (
        <Box sx={{ mt: 2 }}>
          <BankDetailsForm
            userId={user.uid}
            existingDetails={profile?.bankDetails}
          />
        </Box>
      )}
    </Box>
  )

  return (
    <>
      <Wrapper>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant='fullWidth'>
            {availableTabs.map(t => (
              <Tab key={t.value} label={t.label} value={t.value} />
            ))}
          </Tabs>
        </Box>

        {tab === 'dives' && renderDivesTab()}
        {tab === 'invites' && renderInvitesTab()}
        {tab === 'bank' && renderBankTab()}

        <Divider sx={{ my: 4 }} />

        <Stack spacing={2}>
          <Button
            variant='outlined'
            startIcon={<PersonIcon />}
            component={Link}
            to={`/profile/${user?.uid}`}
            fullWidth
          >
            View My Profile
          </Button>
          <Button
            variant='outlined'
            startIcon={<SupportAgentIcon />}
            component={Link}
            to={routes.support}
            fullWidth
          >
            Get Support
          </Button>
          <Button
            variant='outlined'
            color='error'
            startIcon={<LogoutIcon />}
            onClick={handleSignOut}
            fullWidth
          >
            Sign Out
          </Button>
        </Stack>
      </Wrapper>
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity='error' onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </>
  )
}

export default Account
